import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, ArrowLeft, Building2, Database, FolderOpen, Loader2, Monitor, Plus, X } from 'lucide-react';
import { useIsMobile } from '../../hooks/useIsMobile';
import * as service from '../../services/bauordnungService';
import { buildActorName } from '../../services/actorName';
import { fetchProjektMetadaten } from '../../services/asplanService';
import { mitZeitlimit } from '../../services/ablageHilfen';
import { describeError } from '../../services/errorMessage';
import { fetchHandbuch } from '../../services/handbuchService';
import { useProjektSteckbrief } from '../../services/projektSteckbrief';
import type { Report, ReportMetadata, UserProfile } from '../../types';
import { DokumentePanel } from '../dokumente/DokumentePanel';
import { AbweichungsHinweis } from '../common/AbweichungsHinweis';
import { useBauordnung } from './useBauordnung';
import { useFreigabeLauf, type FreigabeAnstoss, type NachJournal } from './useFreigabeLauf';
import { useBauordnungBuchung, type BuchungsErgebnis } from './useBauordnungBuchung';
import type { Bauordnung } from './types';
import { eigenePositionen, istGebucht } from './bauordnungBooking';
import { BauordnungDialog, type AlleBereicheZiel } from './dialog/BauordnungDialog';
import { ladeAnsicht, merkeAnsicht, type BauordnungAnsicht } from './dialog/dialogSpeicher';
import { erzeugteDokumente } from './ablage/bauordnungAblage';
import { freigabeHindernisse, istGesperrt } from './bauordnungLogic';
import { useBestandteilAbschluss } from '../bestandteile/useBestandteilAbschluss';
import { BestandteilAbschlussHinweis } from '../bestandteile/BestandteilAbschlussHinweis';
import { BestandteilStundenPflicht } from '../bestandteile/BestandteilStundenPflicht';
import { bestimmeAbschlussFall, mitPositionsauswahl } from '../bestandteile/abschlussLogic';
import { GrundlagenReiter } from './reiter/GrundlagenReiter';
import { KapitelReiter } from './reiter/KapitelReiter';
import { RollenReiter } from './reiter/RollenReiter';
import { OffenePunkteReiter } from './reiter/OffenePunkteReiter';
import { EigeneKapitelReiter } from './reiter/EigeneKapitelReiter';
import { AusgabeReiter, BauordnungAbschlussDialog } from './reiter/AusgabeReiter';
import type { Dokumentart } from './kapitel';
import type { HandbuchQuelle } from './uebernahmeHandbuch';
import type { ProjektMerkmale } from '../planungsphase/types';

/**
 * Modul 06 „Baustellenordnung + HSE-Plan" — duenner Container.
 *
 * Er ordnet an, zeigt an und ruft auf; er entscheidet nichts. Die Fachlogik
 * liegt in `bauordnungLogic.ts` und `kapitel.ts` und ist dort ohne DOM
 * geprueft — im Repo gibt es kein jsdom, was in einer `.tsx` steht, ist
 * ungeprueft.
 *
 * Der Knopf „Dokumente" oeffnet die zentrale Projektablage, gefiltert auf
 * das Modul `baustellenordnung` — dort liegen die bei der Freigabe abgelegten
 * Fassungen (Plan Stufe 1A.4, Entscheid E14).
 *
 * ═══ WAS DER CONTAINER LIEST — UND NUR LIEST ══════════════════════════════
 * Projektsteckbrief (`useProjektSteckbrief`): Merkmale fuer die bedingten
 * Kapitel, Bauherr/Dritter und Ausfuehrungszeitraum als Vorschlaege fuer die
 * Grundlagen. Das SiGe-Handbuch (`fetchHandbuch`): Quelle der einmaligen
 * Uebernahme. Beides faellt offen aus — ohne Planungsphase bleiben die
 * Merkmale leer (dreiwertig: „kann sein"), ohne Handbuch gibt es nichts zu
 * uebernehmen. Geschrieben wird in keine der beiden Quellen.
 *
 * ═══ AENDERUNGSDIENST (Stufe 6.1) ═════════════════════════════════════════
 * Oben im Inhalt steht der `AbweichungsHinweis`: weicht die Planungsphase vom
 * Stand der freigegebenen Fassung ab (Anker: Journal-Eintrag der Ablage),
 * fragt er nach — „Neue Fassung anlegen" ist `revisionEroeffnen`, derselbe
 * Weg wie der Knopf im Reiter „Ausgabe & Freigabe", und nur an einer
 * freigegebenen Fassung; ein offener Entwurf braucht keine neue.
 *
 * ═══ DER FREIGABE-LAUF LIEGT HIER ════════════════════════════════════════
 * Freigabe, Journal und Ablage (`useFreigabeLauf`) gehoeren dem Container,
 * nicht dem Reiter: der wird beim Reiterwechsel ausgehaengt, der Lauf geht
 * weiter. Sperre und letztes Ergebnis bleiben so sichtbar, und der Hinweis
 * oben sperrt seine Entscheid-Knoepfe, solange der Lauf geht.
 *
 * ═══ ABSCHLUSSSCHRITT NACH DER FREIGABE (Plan Paketposition B5) ════════════
 * Auch die Buchung liegt hier (`useBauordnungBuchung`) — jetzt mit
 * Journalspiegel (Quelle 'bauordnung'). Nach dem Freigabe-Journal fragt das
 * Modul mit den Kennungen der Ausgaben nach, welchen Bestandteil einer
 * Paketposition die Freigabe erledigt hat (`useBestandteilAbschluss`), und
 * oeffnet — gibt es eine EIGENE Position (Fall 1/3) — den Abschlussdialog mit
 * „Leistung buchen". Fall 2 (nur ein Paket): kein Dialog, nur die Meldung
 * ueber den Reitern. Nur-Ansicht: kein Schritt.
 *
 * ═══ ZWEI ANSICHTEN (Plan Baustellenordnung-Assistenz, Stufe B2) ══════════
 * „Geführt" (Vorgabe, je Browser gemerkt) zeigt genau eine Karte
 * (`dialog/BauordnungDialog.tsx`): der Entwurf baut sich selbst, der Mensch
 * beantwortet nur, was kein Projektdatum kennt, und gibt frei. „Alle
 * Bereiche" sind die Reiter wie bisher — Expertenansicht, dieselben
 * Schreibwege. Freigabe-Lauf und Buchung bleiben hier im Container; gibt der
 * Dialog mit gesetztem Buchungshaken frei (E9), bucht er selbst, und der
 * Abschlussdialog bleibt zu (`ohneAbschlussDialog`).
 */

interface Props {
    projectId: string;
    projektName?: string;
    projektNummer?: string;
    projektAdresse?: string;
    /**
     * Das Profil, wie App.tsx es haelt. Der Name des Handelnden wird daraus
     * ueber `buildActorName` gebildet — das Profil traegt Vor- und Nachnamen,
     * kein `full_name`.
     */
    userProfile?: UserProfile | null;
    onBack: () => void;
    isReadOnly?: boolean;
    /**
     * Das Projekt, wie App.tsx es haelt — fuer Steckbrief (Stammdaten) und
     * Leistungsbuchung (`metadata.globalData.services`). Fehlt es, liest der
     * Container die Stammdaten selbst (`fetchProjektMetadaten`); die Buchung
     * braucht zusaetzlich `onUpdate` und bleibt sonst aus.
     */
    report?: Report | null;
    /** Schreibt den gebuchten Leistungsstand ins Projekt (App.handleUpdateReport). */
    onUpdate?: (report: Report) => void;
}

const KEINE_MERKMALE: ProjektMerkmale = {};

type ReiterId = 'grundlagen' | 'kapitel' | 'rollen' | 'offene_punkte' | 'eigene' | 'ausgabe';

const REITER: { id: ReiterId; label: string }[] = [
    { id: 'grundlagen', label: 'Grundlagen' },
    { id: 'kapitel', label: 'Kapitel & Texte' },
    { id: 'rollen', label: 'Organisation' },
    { id: 'offene_punkte', label: 'Offene Punkte' },
    { id: 'eigene', label: 'Eigene Kapitel' },
    { id: 'ausgabe', label: 'Ausgabe & Freigabe' },
];

export const BauordnungModul: React.FC<Props> = ({
    projectId, projektName, projektNummer, projektAdresse, userProfile, onBack, isReadOnly = false,
    report = null, onUpdate,
}) => {
    const isMobile = useIsMobile();
    const handelnder = buildActorName(userProfile);
    const b = useBauordnung(projectId, isReadOnly, handelnder);

    const [reiter, setReiter] = useState<ReiterId>('kapitel');
    const [dokument, setDokument] = useState<Dokumentart>('ordnung');
    const [dokumenteOffen, setDokumenteOffen] = useState(false);

    /** Geführt oder Alle Bereiche — je Browser gemerkt, Vorgabe geführt. */
    const [ansicht, setAnsichtZustand] = useState<BauordnungAnsicht>(() => ladeAnsicht());
    const setAnsicht = (a: BauordnungAnsicht) => {
        setAnsichtZustand(a);
        merkeAnsicht(a);
    };
    /** Aus dem Dialog nach „Alle Bereiche", auf den passenden Reiter. */
    const aufBereich = (ziel?: AlleBereicheZiel) => {
        setAnsicht('alle');
        if (ziel) setReiter(ziel);
    };

    /**
     * Stammdaten fuer den Steckbrief. Liegt das Projekt als Prop vor, sind
     * seine Stammdaten die frischeren (dieselbe Maske daneben); sonst werden
     * sie einmal gelesen. „fuer" im Zustand statt einer Laedt-Flagge — Muster
     * `useProjektBestand` des A+S-Moduls.
     */
    const hatReport = report !== null;
    const [gelesen, setGelesen] = useState<{ fuer: string; metadata: Partial<ReportMetadata> | null } | null>(null);
    useEffect(() => {
        if (hatReport) return;
        let gilt = true;
        // Mit Zeitlimit und fail-open: bleibt die Antwort aus, gilt „gelesen,
        // keine Stammdaten" — sonst stuende „werden noch gelesen" fuer immer,
        // und die Freigabe mit ihm. Ohne Stammdaten bildet der Kern keinen Anker.
        void mitZeitlimit(fetchProjektMetadaten(projectId)).catch(() => null).then(metadata => {
            if (gilt) setGelesen({ fuer: projectId, metadata });
        });
        return () => { gilt = false; };
    }, [projectId, hatReport]);
    const metadata = report?.metadata ?? (gelesen?.fuer === projectId ? gelesen.metadata : null);

    /**
     * Auslöser fuer den Aenderungsdienst (`AbweichungsHinweis`): nach
     * Freigabe-Journal, „Erneut festhalten" und „Neue Fassung eroeffnen"
     * hochgezaehlt, damit der Hinweis die NEUE Fassung vergleicht — nicht die
     * alte, deren Entscheid die frische Freigabe gleich wieder eroeffnete.
     */
    const [abgleichLauf, setAbgleichLauf] = useState(0);
    const abgleichNeu = useCallback(() => setAbgleichLauf(n => n + 1), []);

    /**
     * Abschlussschritt (Plan B5): die Bestandteile nachfragen und — nach einer
     * FREIGABE mit eigener Position — den Buchungsdialog oeffnen. Er erscheint
     * erst, wenn der Lauf (Journal + Ablage) durch ist (`abschlussOffen` und
     * `!freigabeLaeuft` unten), damit er nicht ueber der laufenden Ablage steht.
     */
    const abschluss = useBestandteilAbschluss({
        report, onUpdate, userProfile, quelle: 'baustellenordnung', isReadOnly, projectId,
    });
    const buchung = useBauordnungBuchung({
        kannSchreiben: !!report?.metadata?.globalData && !!onUpdate,
        holeBericht: abschluss.holeBericht,
        schreibeFuer: abschluss.schreibeFuer,
        schreiben: b.schreiben,
        ctx: b.ctx,
    });
    const [abschlussOffen, setAbschlussOffen] = useState(false);
    /**
     * Die Freigabe kam aus dem gefuehrten Dialog MIT gesetztem Buchungshaken
     * (Plan E9): der Dialog bucht selbst, der Abschlussdialog bleibt zu — sonst
     * stuende er ueber einer Buchung, die gerade laeuft. Gilt fuer genau diesen
     * einen Lauf; danach wieder wie bisher.
     */
    const ohneAbschlussDialog = useRef(false);
    const nachAblage = abschluss.nachAblage;
    const nachJournal = useCallback<NachJournal>((pid, ausgaben, anlass) => {
        // Nur fuer das Projekt, das gerade offen ist.
        if (pid !== projectId) return;
        // Nur die EIGENEN Ausgaben loesen aus; aeltere werden nur gemeldet.
        void nachAblage(ausgaben);
        if (anlass === 'freigabe' && !ohneAbschlussDialog.current) setAbschlussOffen(true);
    }, [projectId, nachAblage]);

    /**
     * Der Lauf nach der Freigabe — Journal und Ablage, Sperre und letztes
     * Ergebnis. Hier und nicht im Reiter, damit ein Reiterwechsel ihn nicht
     * vergisst (`useFreigabeLauf`). Solange er laeuft, sind auch die
     * Entscheid-Knoepfe des Hinweises gesperrt.
     */
    const lauf = useFreigabeLauf(projectId, b.bundle?.ordnung ?? null, b.schreiben, abgleichNeu, nachJournal);
    const freigabeLaeuft = lauf.ansicht.laeuft;
    /**
     * Freigabe aus dem gefuehrten Dialog — mit `positionId` bucht das MODUL
     * danach die eine Position (E9), nicht der Dialog: der haengt beim Wechsel
     * nach „Alle Bereiche" aus, das Modul bleibt. Gebucht wird am FREIGEGEBENEN
     * Kopfsatz, frisch gelesen (der Riegel `markiereGebucht` verlangt
     * `status = 'freigegeben'`). Kommt die Buchung an einer FREIGEGEBENEN
     * Fassung nicht zustande — Lesefehler, Buchung nicht erlaubt, `buche`
     * meldet `nicht` oder `uebersprungen` —, oeffnet der Abschlussdialog B5
     * wie bisher, damit nichts stillschweigend ungebucht bleibt. Ist die
     * Fassung dagegen gar nicht freigegeben (Freigabe gescheitert oder
     * uebersprungen), passiert nichts: B5 fuer ein nicht freigegebenes
     * Dokument waere falsch (Codex- und Opus-Review 02.11.2026).
     */
    const freigebenAusDialog = async (a: FreigabeAnstoss, positionId: string | null): Promise<void> => {
        const ordnungId = a.bundle.ordnung?.id ?? null;
        ohneAbschlussDialog.current = !!positionId;
        try {
            await lauf.freigeben(a);
        } finally {
            ohneAbschlussDialog.current = false;
        }
        if (!positionId || !ordnungId) return;
        let neu: Bauordnung | null = null;
        try {
            neu = (await service.fetchBauordnungBundle(projectId)).ordnung;
        } catch (e) {
            console.warn('[Bauordnung] Kopfsatz nach der Freigabe nicht lesbar, Buchung über den Abschlussdialog:', describeError(e));
        }
        if (neu && (neu.id !== ordnungId || neu.status !== 'freigegeben')) return;
        let ergebnis: BuchungsErgebnis = 'uebersprungen';
        if (neu && buchung.darfBuchen) {
            try {
                ergebnis = await buchung.buche(neu, 'ordnung', positionId);
            } catch (e) {
                console.warn('[Bauordnung] Buchung nach der Freigabe nicht ausgeführt:', describeError(e));
            }
        }
        if (ergebnis !== 'gebucht' && ergebnis !== 'bereits') setAbschlussOffen(true);
    };

    /**
     * Der Projektsteckbrief liefert die Merkmale der Planungsphase (sie
     * steuern, welche bedingten Kapitel gezogen werden) und die Vorschlaege
     * fuer die Grundlagen. Fehlt die Planungsphase, bleiben die Merkmale leer
     * — dreiwertig gelesen heisst ein fehlender Schluessel „kann sein", und
     * die bedingten Kapitel stehen sichtbar da, statt still zu fehlen.
     */
    const sb = useProjektSteckbrief(projectId, metadata);
    const merkmale = useMemo(() => sb.steckbrief?.merkmale ?? KEINE_MERKMALE, [sb.steckbrief]);

    /**
     * Das SiGe-Handbuch — Quelle der einmaligen Uebernahme. Drei Lagen, die
     * der Kasten im Grundlagen-Reiter auseinanderhaelt: noch nicht gelesen
     * (`undefined`), gelesen und keins da (`wert: null`, `fehler: null`),
     * nicht lesbar (`fehler`). Ein Lesefehler wird NICHT zu „kein Handbuch" —
     * sonst hiesse es „nichts zu uebernehmen", waehrend es etwas zu holen gab.
     * `handbuchLauf` liest erneut („Erneut laden").
     */
    const [handbuch, setHandbuch] = useState<({ fuer: string } & HandbuchQuelle) | null>(null);
    const [handbuchLauf, setHandbuchLauf] = useState(0);
    useEffect(() => {
        let gilt = true;
        fetchHandbuch(projectId)
            .then(wert => { if (gilt) setHandbuch({ fuer: projectId, wert, fehler: null }); })
            .catch((e: unknown) => { if (gilt) setHandbuch({ fuer: projectId, wert: null, fehler: describeError(e) }); });
        return () => { gilt = false; };
    }, [projectId, handbuchLauf]);
    const handbuchNeuLaden = () => {
        setHandbuch(null);
        setHandbuchLauf(n => n + 1);
    };

    const bundle = b.bundle;
    const ordnung = bundle?.ordnung ?? null;
    const gesperrt = istGesperrt(ordnung);

    // Fall des Abschlussschritts: eigene Position(en) fuer die erzeugten
    // Dokumente (ohne Paketpositionen) und Pakete mit dem Bestandteil
    // „Baustellenordnung". Der Dialog kommt nur bei Fall 1/3 und nur, wenn
    // gebucht werden darf.
    const leistungen = report?.metadata?.globalData?.services ?? [];
    const abschlussFall = ordnung
        ? bestimmeAbschlussFall({ positionen: eigenePositionen(leistungen, erzeugteDokumente(ordnung)), pakete: abschluss.pakete })
        : 'keiner';
    const zeigeAbschlussDialog = abschlussOffen && !freigabeLaeuft && !!ordnung && ordnung.status === 'freigegeben'
        && mitPositionsauswahl(abschlussFall) && buchung.darfBuchen && !abschluss.sperre;

    /** „Neue Fassung anlegen" — vom Hinweis oben und aus dem gefuehrten Dialog derselbe Weg. */
    const neueFassung = gesperrt && !isReadOnly && ordnung
        // `schreiben` meldet den Fehlerfall als `null` — nur dann nicht neu lesen.
        ? () => { void b.schreiben(() => service.revisionEroeffnen(ordnung, b.ctx)).then(p => { if (p !== null) abgleichNeu(); }); }
        : undefined;
    /** Den Abschlussdialog von Hand oeffnen (gefuehrter Dialog) — nur, wenn dort noch etwas zu buchen ist. */
    const buchenOeffnen = ordnung && ordnung.status === 'freigegeben' && mitPositionsauswahl(abschlussFall)
        && buchung.darfBuchen && !abschluss.sperre && erzeugteDokumente(ordnung).some(art => !istGebucht(ordnung, art))
        ? () => setAbschlussOffen(true)
        : undefined;
    const offeneHindernisse = useMemo(
        () => (bundle ? freigabeHindernisse(bundle, merkmale).length : 0),
        [bundle, merkmale],
    );

    // ═══ Das Telefon ══════════════════════════════════════════════════════
    // Die Kachel ist auf kleinen Bildschirmen ausgeblendet — die Ansicht
    // dahinter ist damit nicht unerreichbar (gemerkte Ansicht nach F5, jeder
    // Ruecksprung). Kein automatischer Ruecksprung: eine Ansicht, die sich
    // beim Oeffnen selbst schliesst, sieht wie ein Fehler aus.
    if (isMobile) {
        return (
            <div data-glass-view="module-bauordnung" data-glass-bereich="module" className="h-full overflow-y-auto bg-slate-50 p-5 animate-fade-in">
                <div className="max-w-md mx-auto pt-10 text-center">
                    <div className="w-14 h-14 rounded-2xl bg-white border border-slate-200 flex items-center justify-center mx-auto mb-4">
                        <Monitor className="w-7 h-7 text-slate-400" />
                    </div>
                    <h1 className="text-lg font-bold text-slate-800">Baustellenordnung &amp; HSE-Plan</h1>
                    <p className="text-sm text-slate-500 mt-2 leading-relaxed">
                        Beide Dokumente werden am Rechner erstellt: Kapitel pflegen, Texte schreiben und die
                        Datei erzeugen ist Schreibtischarbeit und auf dem Telefon nicht bedienbar.
                    </p>
                    <div className="mt-5 bg-white border border-slate-200 rounded-2xl p-4 text-left">
                        <p className="text-[11px] font-black uppercase tracking-[0.16em] text-slate-400 mb-2">Stand</p>
                        <dl className="text-sm text-slate-600 space-y-1">
                            <div className="flex justify-between gap-3">
                                <dt className="text-slate-400">Projekt</dt>
                                <dd className="font-semibold text-slate-700 text-right">{projektName || 'keine Angabe'}</dd>
                            </div>
                            <div className="flex justify-between gap-3">
                                <dt className="text-slate-400">Fassung</dt>
                                <dd className="font-semibold text-slate-700">
                                    {ordnung ? (ordnung.version_label || 'Entwurf') : 'noch keine'}
                                </dd>
                            </div>
                            <div className="flex justify-between gap-3">
                                <dt className="text-slate-400">Offene Pflichtpunkte</dt>
                                <dd className="font-semibold text-slate-700">{ordnung ? offeneHindernisse : 'keine Angabe'}</dd>
                            </div>
                        </dl>
                    </div>
                    <button onClick={onBack}
                        className="mt-5 w-full bg-slate-800 text-white rounded-xl px-4 py-3 text-sm font-bold hover:bg-slate-900">
                        Zurück zur Projektübersicht
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div data-glass-view="module-bauordnung" data-glass-bereich="module" className="h-full overflow-y-auto bg-slate-50 animate-fade-in">
            <div data-glass="kopf" className="bg-white border-b border-slate-200 sticky top-0 z-10">
                <div className="max-w-6xl mx-auto px-6 py-4 flex items-center gap-4">
                    <button onClick={onBack} className="p-2 rounded-lg hover:bg-slate-100 text-slate-500 shrink-0" title="Zurück">
                        <ArrowLeft className="w-5 h-5" />
                    </button>
                    <div className="min-w-0 flex-1">
                        <h1 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                            <Building2 className="w-5 h-5 text-fuchsia-600 shrink-0" />
                            Baustellenordnung &amp; HSE-Plan
                        </h1>
                        <p className="text-[11px] text-slate-500 truncate">
                            {projektNummer ? `${projektNummer} · ` : ''}{projektName || 'Projekt'}
                            {isReadOnly && ' · Nur-Ansicht'}
                            {gesperrt && ' · freigegeben'}
                        </p>
                    </div>
                    {b.arbeitet && <Loader2 className="w-4 h-4 text-slate-400 animate-spin shrink-0" />}
                    {/* Umschalter der Ansicht (Plan E4): Vorgabe geführt, die Wahl
                        merkt der Browser. Beide Ansichten lesen denselben Stand —
                        umschalten verliert nichts; eine laufende KI-Durchsicht
                        bricht ab und wird beim naechsten Oeffnen wiederholt. */}
                    <div data-glass="umschalter" role="group" aria-label="Ansicht" className="inline-flex bg-slate-100 p-0.5 rounded-lg border border-slate-200 shrink-0">
                        {([
                            { art: 'gefuehrt', label: 'Geführt' },
                            { art: 'alle', label: 'Alle Bereiche' },
                        ] as const).map(a => (
                            <button
                                key={a.art}
                                type="button"
                                onClick={() => setAnsicht(a.art)}
                                aria-pressed={ansicht === a.art}
                                className={`px-3 py-1.5 rounded-md text-xs font-bold whitespace-nowrap ${
                                    ansicht === a.art ? 'bg-slate-800 text-white' : 'text-slate-500 hover:text-slate-700'
                                }`}
                            >
                                {a.label}
                            </button>
                        ))}
                    </div>
                    <button
                        onClick={() => setDokumenteOffen(true)}
                        className="text-sm font-bold text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 rounded-lg px-3 py-2 flex items-center gap-1.5 shrink-0"
                        title="Abgelegte Fassungen und zugeordnete Dokumente aus der Projektablage"
                    >
                        <FolderOpen className="w-4 h-4" /> Dokumente
                    </button>
                </div>
                {ordnung && ansicht === 'alle' && (
                    <div data-glass="reiter" className="max-w-6xl mx-auto px-6 flex gap-1 overflow-x-auto">
                        {REITER.map(r => (
                            <button
                                key={r.id}
                                onClick={() => setReiter(r.id)}
                                className={`px-3.5 py-2.5 text-sm font-bold border-b-2 whitespace-nowrap transition-colors ${
                                    reiter === r.id
                                        ? 'border-fuchsia-500 text-fuchsia-600'
                                        : 'border-transparent text-slate-400 hover:text-slate-600'
                                }`}
                            >
                                {r.label}
                                {r.id === 'offene_punkte' && bundle && bundle.luecken.some(l => !l.erledigt_am) && (
                                    <span className="ml-1.5 text-[10px] bg-amber-100 text-amber-700 rounded px-1.5 py-0.5">
                                        {bundle.luecken.filter(l => !l.erledigt_am).length}
                                    </span>
                                )}
                            </button>
                        ))}
                    </div>
                )}
            </div>

            <div className="max-w-6xl mx-auto px-6 py-6 space-y-5 pb-20">
                {!b.laedt && ordnung && (
                    <AbweichungsHinweis
                        projectId={projectId}
                        metadata={metadata}
                        unterlage="baustellenordnung"
                        isReadOnly={isReadOnly}
                        onNeueFassung={neueFassung}
                        neuladen={abgleichLauf}
                        sperrgrund={freigabeLaeuft
                            ? 'Die Freigabe wird noch festgehalten und abgelegt. Ein Entscheid ist erst danach möglich.'
                            : null}
                    />
                )}

                {/* Plan B5, Fall 2/3: was die Freigabe an Bestandteilen erledigt hat —
                    ueber den Reitern, solange der Abschlussdialog es nicht zeigt. */}
                {!zeigeAbschlussDialog && (
                    <BestandteilAbschlussHinweis stand={abschluss.stand} onSchliessen={abschluss.zuruecksetzen} />
                )}

                {/* Im gefuehrten Dialog steht der Fehler auf der Karte, die geschrieben hat. */}
                {b.fehler && ansicht === 'alle' && (
                    <div className="bg-rose-50 border border-rose-200 rounded-xl p-3 flex items-start gap-2">
                        <AlertTriangle className="w-4 h-4 text-rose-600 shrink-0 mt-0.5" />
                        <p className="text-sm text-rose-800 flex-1">{b.fehler}</p>
                        <button onClick={b.fehlerLoeschen} className="text-rose-400 hover:text-rose-600">
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                )}

                {b.laedt && (
                    <div className="flex items-center gap-2 text-sm text-slate-400 py-10 justify-center">
                        <Loader2 className="w-4 h-4 animate-spin" /> Lade…
                    </div>
                )}

                {/* Die Tabellen fehlen noch — das sagt die Ansicht ausdruecklich,
                    statt leer zu bleiben. */}
                {!b.laedt && bundle?.schemaFehlt && (
                    <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 flex gap-3">
                        <Database className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                        <div className="text-sm text-amber-900">
                            <p className="font-bold">Die Datenbank kennt dieses Modul noch nicht.</p>
                            <p className="mt-1 text-[13px] leading-relaxed">
                                Migration <strong>Nr. 43</strong> (<code>supabase/bauordnung.sql</code>) ist auf
                                dieser Umgebung noch nicht gelaufen. Sie legt die sechs Tabellen an; danach
                                lässt sich hier arbeiten. Bis dahin wird bewusst nichts gespeichert, denn eine
                                Eingabe wäre beim nächsten Laden weg.
                            </p>
                        </div>
                    </div>
                )}

                {/* Der gefuehrte Weg: genau eine Karte. Je Projekt neu eingehaengt
                    (`key`) — so beginnt jedes Projekt mit einer leeren Sitzung, und
                    kein Lauf des vorigen schreibt in dieses. */}
                {ansicht === 'gefuehrt' && !b.laedt && bundle && !bundle.schemaFehlt && (
                    <BauordnungDialog
                        key={projectId}
                        projectId={projectId}
                        projektName={projektName || ''}
                        projektNummer={projektNummer}
                        projektAdresse={projektAdresse}
                        bundle={bundle}
                        merkmale={merkmale}
                        steckbrief={sb.steckbrief}
                        steckbriefBereit={sb.bereit}
                        handbuch={handbuch?.fuer === projectId ? handbuch : undefined}
                        b={b}
                        lauf={lauf.ansicht}
                        freigeben={freigebenAusDialog}
                        buchung={buchung}
                        services={leistungen}
                        metadata={metadata}
                        stammdatenLaden={!hatReport && gelesen?.fuer !== projectId}
                        firma={userProfile?.company_name ?? ''}
                        handelnder={handelnder}
                        nurAnsicht={isReadOnly}
                        aufBereich={aufBereich}
                        onNeueFassung={neueFassung}
                        onBuchen={buchenOeffnen}
                    />
                )}

                {/* Beim Oeffnen wird NICHTS angelegt. Erst dieser Klick legt den
                    Kopfsatz an — sonst traegt jedes angesehene Projekt eine
                    Baustellenordnung, die niemand wollte. */}
                {ansicht === 'alle' && !b.laedt && bundle && !bundle.schemaFehlt && !ordnung && (
                    <div className="bg-white border border-slate-200 rounded-2xl p-6 max-w-2xl">
                        <h2 className="text-base font-bold text-slate-800">
                            Für dieses Projekt gibt es noch keine Baustellenordnung.
                        </h2>
                        <p className="text-sm text-slate-500 mt-2 leading-relaxed">
                            Sie wird erst auf Klick angelegt. Ein bloßes Ansehen soll kein Dokument erzeugen.
                            Danach stehen Kapitel, Texte, Organisation, offene Punkte und die Ausgabe zur
                            Verfügung.
                        </p>
                        <button
                            onClick={() => b.schreiben(() => service.ensureBauordnung(projectId, b.ctx, {
                                geltung_ort: projektAdresse || '',
                            }))}
                            disabled={isReadOnly}
                            className="mt-4 bg-fuchsia-600 text-white text-sm font-bold rounded-lg px-4 py-2.5 flex items-center gap-1.5 hover:bg-fuchsia-700 disabled:bg-slate-200 disabled:text-slate-400"
                        >
                            <Plus className="w-4 h-4" /> Baustellenordnung anlegen
                        </button>
                        {isReadOnly && (
                            <p className="text-[12px] text-slate-400 mt-2">
                                Das Projekt ist nur zur Ansicht geöffnet.
                            </p>
                        )}
                    </div>
                )}

                {ansicht === 'alle' && !b.laedt && bundle && ordnung && (
                    <>
                        {reiter === 'kapitel' && (
                            <div className="flex items-center gap-1 mb-1">
                                {(['ordnung', 'hse'] as Dokumentart[])
                                    .filter(d => (d === 'ordnung' ? ordnung.erzeugt_ordnung : ordnung.erzeugt_hse))
                                    .map(d => (
                                        <button
                                            key={d}
                                            onClick={() => setDokument(d)}
                                            className={`text-[12px] font-bold rounded-lg px-3 py-1.5 border ${
                                                dokument === d
                                                    ? 'bg-slate-800 text-white border-slate-800'
                                                    : 'bg-white text-slate-500 border-slate-200 hover:border-slate-300'
                                            }`}
                                        >
                                            {d === 'ordnung' ? 'Baustellenordnung' : 'HSE-Plan'}
                                        </button>
                                    ))}
                            </div>
                        )}

                        {reiter === 'grundlagen' && (
                            <GrundlagenReiter
                                bundle={bundle} ctx={b.ctx} gesperrt={gesperrt} schreiben={b.schreiben}
                                steckbrief={sb.steckbrief}
                                handbuch={handbuch?.fuer === projectId ? handbuch : undefined}
                                onHandbuchNeuLaden={handbuchNeuLaden}
                            />
                        )}
                        {reiter === 'kapitel' && (
                            <KapitelReiter
                                bundle={bundle}
                                dokument={ordnung.erzeugt_ordnung ? dokument : 'hse'}
                                merkmale={merkmale}
                                ctx={b.ctx} gesperrt={gesperrt} schreiben={b.schreiben}
                            />
                        )}
                        {reiter === 'rollen' && (
                            <RollenReiter bundle={bundle} ctx={b.ctx} gesperrt={gesperrt} schreiben={b.schreiben} />
                        )}
                        {reiter === 'offene_punkte' && (
                            <OffenePunkteReiter bundle={bundle} ctx={b.ctx} gesperrt={gesperrt} schreiben={b.schreiben} />
                        )}
                        {reiter === 'eigene' && (
                            <EigeneKapitelReiter bundle={bundle} ctx={b.ctx} schreiben={b.schreiben} />
                        )}
                        {reiter === 'ausgabe' && (
                            <AusgabeReiter
                                bundle={bundle} merkmale={merkmale} ctx={b.ctx}
                                projektName={projektName || ''} projektNummer={projektNummer}
                                projektAdresse={projektAdresse}
                                schreiben={b.schreiben}
                                aufReiter={r => setReiter(r as ReiterId)}
                                aufDokumente={() => setDokumenteOffen(true)}
                                report={report}
                                onUpdate={onUpdate}
                                buchung={buchung}
                                // Stammdaten fuer den Planungsstand der Freigabe — dieselben wie
                                // fuer den Steckbrief, einmal gelesen (Stufe 6.1).
                                metadata={metadata}
                                stammdatenLaden={!hatReport && gelesen?.fuer !== projectId}
                                lauf={lauf.ansicht}
                                onFreigeben={a => { void lauf.freigeben(a); }}
                                onAblegen={a => { void lauf.ablegen(a); }}
                                onFesthalten={(fuer, auftrag) => { void lauf.festhalten(fuer, auftrag); }}
                                onAbgleichNoetig={abgleichNeu}
                            />
                        )}
                    </>
                )}
            </div>

            {/* Stunden-Pflichtfenster (Stundenkontingent S3) — genau EINMAL je Modul,
                nicht im Hinweis: der steht im Abschlussdialog ODER über den Reitern. */}
            <BestandteilStundenPflicht pflicht={abschluss.stundenPflicht} />

            {zeigeAbschlussDialog && ordnung && (
                <BauordnungAbschlussDialog
                    o={ordnung}
                    services={leistungen}
                    buchung={buchung}
                    bestandteile={abschluss.stand}
                    onSchliessen={() => setAbschlussOffen(false)}
                />
            )}

            {dokumenteOffen && (
                <DokumentePanel
                    projectId={projectId}
                    modul="baustellenordnung"
                    userProfile={userProfile}
                    isReadOnly={isReadOnly}
                    onSchliessen={() => setDokumenteOffen(false)}
                />
            )}
        </div>
    );
};
